import { ActionIcon, Anchor, Avatar, Badge, Loader, Modal, Text, Tooltip } from '@mantine/core';
import { useModals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconArrowRight,
  IconArrowsLeftRight,
  IconBuildingBank,
  IconCircleCheck,
  IconClock,
  IconPaperclip,
  IconPencil,
  IconTrash,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import { DataTable } from 'mantine-datatable';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BudgetSelectBanner } from 'src/Components/Expenses/BudgetSelectBanner';
import { ShareAvatar } from 'src/Components/Expenses/ShareAvatar';
import { EditOccurrenceAmountModal } from 'src/Components/Modals/EditOccurrenceAmountModal';
import { MarkReimbursedModal } from 'src/Components/Modals/MarkReimbursedModal';
import { MoneyTransferDetailModal } from 'src/Components/Modals/MoneyTransferDetailModal';
import UserAvatar from 'src/Components/NavBar/UserAvatar';
import { AttachmentsViewer } from 'src/Components/Shared/AttachmentsViewer';
import { BankLogo } from 'src/Components/Shared/BankLogo';
import { RowAction, RowActions } from 'src/Components/Shared/RowActions';
import { useDeleteExpense } from 'src/Hooks/Mutations/useDeleteExpense';
import { useDeleteMoneyTransfer } from 'src/Hooks/Mutations/useDeleteMoneyTransfer';
import { useMarkExpensesReimbursed } from 'src/Hooks/Mutations/useMarkExpensesReimbursed';
import { useSetRecurrenceOccurrenceAmount } from 'src/Hooks/Mutations/useSetRecurrenceOccurrenceAmount';
import useFetchBankAccounts from 'src/Hooks/Queries/useFetchBankAccounts';
import useFetchExpense from 'src/Hooks/Queries/useFetchExpense';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import useNavigateKeepingSearch from 'src/Hooks/Shared/useNavigateKeepingSearch';
import useSortableRecords from 'src/Hooks/Shared/useSortableRecords';
import {
  trackExpenseReceiptOpenedFromList,
  trackExpensesMarkedReimbursed,
  trackRecurrenceOccurrenceAmountSet,
} from 'src/Services/Mixpanel/events';
import {
  MoneyTransferDto,
  SettlementTargetDto,
  isPersonTarget,
} from 'src/types/money-transfer.types';
import { getCategoryIcon } from 'src/Utils/categoryIcons';
import { getMainCategoryForType } from 'src/Utils/expenseCategories';

import styles from './BudgetList.module.scss';
import { EnrichedBudgetEntry, Props } from './BudgetList.types';

/**
 * Spells out where the "Pour moi" figure comes from, so a number like "+200 €" on a 300 €
 * expense stops looking arbitrary.
 */
const buildNetImpactTooltip = (
  record: EnrichedBudgetEntry,
  t: TFunction<'translation', undefined>,
  currentUserId?: string
): string => {
  const format = (value: number) => `${value.toFixed(2)} €`;
  const impact = record.netImpact ?? 0;
  const signed = impact > 0 ? `+${format(impact)}` : format(impact);

  if (record.kind === 'transfer') {
    return impact > 0
      ? t('budget.netImpact.transferReceived', 'Vous avez reçu {{amount}} → {{impact}}', {
          amount: format(record.amount),
          impact: signed,
        })
      : t('budget.netImpact.transferSent', 'Vous avez envoyé {{amount}} → {{impact}}', {
          amount: format(record.amount),
          impact: signed,
        });
  }

  const share = format(record.shares?.find(s => s.user?.id === currentUserId)?.amount ?? 0);
  const isPaidByMe = record.paidBy?.id === currentUserId;
  // On incomes the tooltip talks about the collector (« Encaissé par X »), on expenses
  // about the payer. payerName is the share owner on incomes, so pick collectorName there.
  const payerLabel = (record.isIncome ? record.collectorName : record.payerName) ?? '—';
  const values = {
    amount: format(record.amount),
    share,
    impact: signed,
    payer: payerLabel,
  };

  if (record.isIncome) {
    const outcome =
      impact < 0
        ? t('budget.netImpact.toRedistribute', 'à redistribuer')
        : t('budget.netImpact.toReceive', 'à recevoir');
    return isPaidByMe
      ? t(
          'budget.netImpact.incomeCollectedByMe',
          'Vous avez encaissé {{amount}} · votre quote-part {{share}} → {{impact}} {{outcome}}',
          { ...values, outcome }
        )
      : t(
          'budget.netImpact.incomeCollectedBy',
          'Encaissé par {{payer}} · votre quote-part {{share}} → {{impact}} {{outcome}}',
          { ...values, outcome }
        );
  }

  const outcome =
    impact > 0
      ? t('budget.netImpact.toRecover', 'à récupérer')
      : impact < 0
        ? t('budget.netImpact.youOwe', 'que vous devez')
        : t('budget.netImpact.settled', 'solde nul');

  return isPaidByMe
    ? t(
        'budget.netImpact.paidByMe',
        'Vous avez payé {{amount}} · votre quote-part {{share}} → {{impact}} {{outcome}}',
        { ...values, outcome }
      )
    : t(
        'budget.netImpact.paidBy',
        'Payé par {{payer}} · votre quote-part {{share}} → {{impact}} {{outcome}}',
        { ...values, outcome }
      );
};

const BudgetList = ({
  entries,
  loading,
  fetchingMore,
  currentUserId,
  onRowClick,
  onResetFilters,
  hasActiveFilters,
}: Props) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const navigate = useNavigateKeepingSearch();
  const modals = useModals();
  const { mutate: deleteTransfer } = useDeleteMoneyTransfer();
  const { mutate: deleteExpense } = useDeleteExpense();
  const assetAboveQuotas = useIsAssetAboveQuotas();
  const { sortedRecords, sortStatus, setSortStatus } = useSortableRecords(entries);
  const [selectedExpenses, setSelectedExpenses] = useState<EnrichedBudgetEntry[]>([]);
  const [isMarkReimbursedModalOpen, setIsMarkReimbursedModalOpen] = useState(false);
  const { mutate: markExpensesReimbursed, isLoading: isMarkReimbursedLoading } =
    useMarkExpensesReimbursed();
  const [editingOccurrence, setEditingOccurrence] = useState<EnrichedBudgetEntry | null>(null);
  const [detailTransfer, setDetailTransfer] = useState<MoneyTransferDto | null>(null);
  const { mutate: setOccurrenceAmount, isLoading: isSetOccurrenceAmountLoading } =
    useSetRecurrenceOccurrenceAmount();

  // The paperclip opens the receipt viewer without navigating to the expense sheet.
  // We load the full expense on click (the list DTO only carries attachmentsCount)
  // and mount the viewer once its attachments are available.
  const [quickViewExpenseId, setQuickViewExpenseId] = useState<string | null>(null);
  const {
    data: quickViewExpense,
    isLoading: isQuickViewLoading,
    isError: isQuickViewError,
  } = useFetchExpense(quickViewExpenseId ?? undefined);
  const isViewerOpen = !!quickViewExpenseId && !!quickViewExpense;

  useEffect(() => {
    if (!isQuickViewError || !quickViewExpenseId) return;
    notifications.show({
      message: t('expenses.list.openReceiptError', 'Impossible de charger le justificatif.'),
      color: 'red',
    });
    setQuickViewExpenseId(null);
  }, [isQuickViewError, quickViewExpenseId, t]);

  const handleReceiptClick = (event: React.MouseEvent, expenseId: string, count: number) => {
    event.stopPropagation();
    setQuickViewExpenseId(expenseId);
    trackExpenseReceiptOpenedFromList({ count });
  };

  // ExpensePreviewBankAccountDto only carries { id, name }; the brand logo lives on
  // BankAccountDto (bankDomain/bankName). Join on the cached list — no extra request.
  const { data: bankAccounts } = useFetchBankAccounts();
  const bankAccountsById = useMemo(() => {
    const map = new Map<string, { bankDomain?: string; bankName?: string }>();
    (bankAccounts ?? []).forEach(acc => {
      map.set(acc.id, { bankDomain: acc.bankDomain, bankName: acc.bankName });
    });
    return map;
  }, [bankAccounts]);

  const renderBankAccountIcon = (bankAccountId: string) => {
    const acc = bankAccountsById.get(bankAccountId);
    if (acc?.bankDomain) {
      return <BankLogo domain={acc.bankDomain} name={acc.bankName} size="sm" />;
    }
    return <IconBuildingBank size={20} color="gray" />;
  };

  const isExpenseSelectable = (record: EnrichedBudgetEntry) =>
    record.kind === 'expense' &&
    !record.isVirtual &&
    record.isReimbursed === false &&
    !!(record.paidBy ?? record.paidByBankAccount);

  // A user target can have an empty firstName/lastName (invited member who hasn't
  // completed their profile yet), the backend has no other name to fall back on.
  const getSettlementTargetName = (target: SettlementTargetDto) =>
    isPersonTarget(target)
      ? [target.firstName, target.lastName].filter(Boolean).join(' ') ||
        t('checklist.invitedUser', 'Invité')
      : target.name;

  const selectedTotalAmount = selectedExpenses.reduce((sum, entry) => sum + entry.amount, 0);

  const handleMarkReimbursed = (createTransfers: boolean) => {
    const expenseIds = selectedExpenses.map(entry => entry.id);
    const totalAmount = selectedTotalAmount;
    markExpensesReimbursed(
      { expenseIds, createTransfers },
      {
        onSuccess: () => {
          trackExpensesMarkedReimbursed({
            expenses_count: expenseIds.length,
            total_amount: totalAmount,
            transfers_created: createTransfers,
          });
          notifications.show({
            message: t(
              'expenses.markReimbursed.success',
              'Les dépenses ont été marquées comme remboursées.'
            ),
            color: 'teal',
          });
          setIsMarkReimbursedModalOpen(false);
          setSelectedExpenses([]);
        },
      }
    );
  };

  const handleConfirmOccurrenceAmount = (amount: number) => {
    if (!editingOccurrence || editingOccurrence.kind === 'transfer') return;
    const templateExpenseId = editingOccurrence.templateExpenseId;
    const date = editingOccurrence.occurrenceDate;
    if (!templateExpenseId || !date) return;

    setOccurrenceAmount(
      { templateExpenseId, date, amount },
      {
        onSuccess: () => {
          trackRecurrenceOccurrenceAmountSet();
          notifications.show({
            message: t('expenses.editOccurrenceAmount.success', 'Montant mis à jour.'),
            color: 'teal',
          });
          setEditingOccurrence(null);
        },
      }
    );
  };

  const handleDeleteTransfer = (id: string) => {
    modals.openConfirmModal({
      title: t('moneyTransfer.delete.confirmTitle', 'Supprimer le remboursement'),
      children: (
        <Text size="sm">
          {t(
            'moneyTransfer.delete.confirmMessage',
            'Êtes-vous sûr de vouloir supprimer ce remboursement ? Cette action est irréversible.'
          )}
        </Text>
      ),
      labels: {
        confirm: t('common.delete', 'Supprimer'),
        cancel: t('common.cancel', 'Annuler'),
      },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteTransfer(id),
    });
  };

  const handleDeleteExpense = (id: string) => {
    modals.openConfirmModal({
      title: t('expense.delete.confirmTitle', 'Supprimer la dépense'),
      children: (
        <Text size="sm">
          {t(
            'expense.delete.confirmMessage',
            'Êtes-vous sûr de vouloir supprimer cette dépense ? Cette action est irréversible.'
          )}
        </Text>
      ),
      labels: {
        confirm: t('common.delete', 'Supprimer'),
        cancel: t('common.cancel', 'Annuler'),
      },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteExpense(id),
    });
  };

  return (
    <>
      <DataTable
        withRowBorders={false}
        style={{ width: '100%', minHeight: '10rem' }}
        records={sortedRecords}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        selectedRecords={selectedExpenses}
        onSelectedRecordsChange={setSelectedExpenses}
        isRecordSelectable={isExpenseSelectable}
        rowStyle={() => ({ height: 58 })}
        rowClassName={record => {
          if (record.kind === 'transfer') return undefined;
          if (record.isVirtual) return styles.virtual;
          return undefined;
        }}
        {...(hasActiveFilters
          ? {
              emptyState: (
                <div className={styles.emptyState}>
                  <Text size="sm" c="dimmed">
                    {t('expenses.filters.empty', 'Aucune opération ne correspond à ces filtres')}
                  </Text>
                  <Anchor component="button" type="button" size="sm" onClick={onResetFilters}>
                    {t('expenses.filters.reset', 'Réinitialiser')}
                  </Anchor>
                </div>
              ),
            }
          : { noRecordsText: t('expenses.empty', 'Aucune opération') })}
        fetching={loading}
        onRowClick={({ record }) => {
          if (record.kind !== 'transfer' && record.isVirtual) {
            if (record.templateExpenseId) setEditingOccurrence(record);
            return;
          }
          if (onRowClick) {
            onRowClick(record);
          } else if (record.kind === 'transfer') {
            // A transfer has no page of its own: its detail — including the expenses it
            // settles — is shown in a modal.
            setDetailTransfer(record);
          } else {
            navigate(`${record.id}`);
          }
        }}
        columns={[
          {
            // dateObj, not date: `date` is a Dayjs on expenses and an ISO string on
            // transfers, so sorting on it mixes two incomparable shapes.
            accessor: 'dateObj',
            title: t('common.date', 'Date'),
            sortable: true,
            width: 100,
            render: ({ dateObj }) => dayjs(dateObj).format('DD/MM/YYYY'),
          },
          {
            // sortTitle, not title: transfers have no `title` of their own and would all
            // pile up at one end of the sort.
            accessor: 'sortTitle',
            title: t('common.description', 'Description'),
            sortable: true,
            render: record => {
              if (record.kind === 'transfer') {
                const fromName = getSettlementTargetName(record.from);
                const toName = getSettlementTargetName(record.to);
                return (
                  <div className={styles.column}>
                    <div className={styles.title}>
                      {t('expenses.transferTitle', 'Remboursement')}
                    </div>
                    {isMobile && (
                      <div className={styles.paidByLine}>
                        <span className={styles.paidBy}>{fromName}</span>
                        <IconArrowRight size={12} style={{ margin: '0 4px' }} />
                        <span className={styles.paidBy}>{toName}</span>
                      </div>
                    )}
                  </div>
                );
              }

              const paidByLabel = record.isIncome
                ? t('expenses.list.receivedBy', 'Encaissé par ')
                : t('expenses.list.paidBy', 'Payé par ');

              const paidByName = record.paidBy
                ? [record.paidBy?.firstName, record.paidBy?.lastName].join(' ')
                : record.paidByBankAccount?.name;

              const attachmentsCount = record.attachmentsCount ?? 0;
              const isRowLoadingReceipt = quickViewExpenseId === record.id && isQuickViewLoading;

              return (
                <div className={styles.column}>
                  <div className={styles.title}>
                    {record.title}
                    {isMobile && attachmentsCount > 0 && (
                      <Tooltip
                        label={t('expenses.list.attachmentsCount', '{{count}} justificatif(s)', {
                          count: attachmentsCount,
                        })}
                      >
                        <ActionIcon
                          variant="transparent"
                          size="lg"
                          loading={isRowLoadingReceipt}
                          aria-label={t('expenses.list.openReceipt', 'Voir le justificatif')}
                          onClick={event => handleReceiptClick(event, record.id, attachmentsCount)}
                          style={{ marginLeft: 4, verticalAlign: 'middle' }}
                        >
                          <IconPaperclip size={16} color="var(--mantine-color-gray-6)" />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </div>
                  {isMobile && (
                    <div className={styles.paidByLine}>
                      <span className={styles.paidBy}>{paidByLabel}</span>
                      <span className={styles.paidByName}>{paidByName}</span>
                    </div>
                  )}
                </div>
              );
            },
          },
          {
            accessor: 'payerName',
            title: t('common.payer', 'Payeur'),
            sortable: true,
            hidden: isMobile,
            render: record => {
              if (record.kind === 'transfer') {
                const fromIsUser = isPersonTarget(record.from);
                const fromName = getSettlementTargetName(record.from);
                return (
                  <div className={styles.payerCell}>
                    {fromIsUser ? (
                      <UserAvatar
                        user={{
                          id: record.from.id,
                          firstName: record.from.firstName ?? '',
                          lastName: record.from.lastName ?? '',
                        }}
                        size="sm"
                      />
                    ) : (
                      renderBankAccountIcon(record.from.id)
                    )}
                    <span className={styles.payerName}>{fromName}</span>
                  </div>
                );
              }

              // Income: the payer is the sole share owner (fund-call pattern). Any other
              // shape (0 or >1 shares, share without user) has no meaningful payer.
              if (record.isIncome) {
                const shares = record.shares ?? [];
                const singleShare = shares.length === 1 ? shares[0] : null;
                if (singleShare?.user) {
                  const firstName = singleShare.user.firstName?.trim() ?? '';
                  const displayName =
                    firstName.length > 0 ? firstName : t('checklist.invitedUser', 'Invité');
                  return (
                    <div className={styles.payerCell}>
                      <UserAvatar user={singleShare.user} size="sm" />
                      <span className={styles.payerName}>{displayName}</span>
                    </div>
                  );
                }
                return (
                  <Text size="sm" c="dimmed">
                    —
                  </Text>
                );
              }

              if (record.paidBy) {
                return (
                  <div className={styles.payerCell}>
                    <UserAvatar user={record.paidBy} size="sm" />
                    <span className={styles.payerName}>{record.paidBy.firstName}</span>
                  </div>
                );
              }

              if (record.paidByBankAccount) {
                return (
                  <div className={styles.payerCell}>
                    {renderBankAccountIcon(record.paidByBankAccount.id)}
                    <span className={styles.payerName}>{record.paidByBankAccount.name}</span>
                  </div>
                );
              }

              if (record.paidByContact) {
                const contactName = record.paidByContact.displayName;
                return (
                  <div className={styles.payerCell}>
                    <span className={styles.payerName}>{contactName}</span>
                  </div>
                );
              }

              if (record.paidByPlaceholderMember) {
                const placeholderName =
                  [
                    record.paidByPlaceholderMember.firstName,
                    record.paidByPlaceholderMember.lastName,
                  ]
                    .filter(Boolean)
                    .join(' ') || record.paidByPlaceholderMember.firstName;
                return (
                  <div className={styles.payerCell}>
                    <span className={styles.payerName}>{placeholderName}</span>
                  </div>
                );
              }

              return null;
            },
          },
          {
            accessor: 'receiver',
            title: t('common.receiver', 'Receveur'),
            hidden: isMobile,
            render: record => {
              if (record.kind === 'transfer') {
                const toIsUser = isPersonTarget(record.to);
                const toName = getSettlementTargetName(record.to);
                return (
                  <div className={styles.payerCell}>
                    {toIsUser ? (
                      <UserAvatar
                        user={{
                          id: record.to.id,
                          firstName: record.to.firstName ?? '',
                          lastName: record.to.lastName ?? '',
                        }}
                        size="sm"
                      />
                    ) : (
                      renderBankAccountIcon(record.to.id)
                    )}
                    <span className={styles.payerName}>{toName}</span>
                  </div>
                );
              }

              // Income: show who actually collected the money. Expenses have no receiver
              // column value (money leaves, it doesn't land somewhere trackable here).
              if (record.isIncome) {
                if (record.paidBy) {
                  return (
                    <div className={styles.payerCell}>
                      <UserAvatar user={record.paidBy} size="sm" />
                      <span className={styles.payerName}>{record.paidBy.firstName}</span>
                    </div>
                  );
                }
                if (record.paidByBankAccount) {
                  return (
                    <div className={styles.payerCell}>
                      {renderBankAccountIcon(record.paidByBankAccount.id)}
                      <span className={styles.payerName}>{record.paidByBankAccount.name}</span>
                    </div>
                  );
                }
                return (
                  <Text size="sm" c="dimmed">
                    —
                  </Text>
                );
              }

              return null;
            },
          },
          {
            // categoryLabel, not type: `type` is the raw enum key (ELECTRICITY, ROOFING…),
            // so sorting on it has nothing to do with the French labels on screen.
            accessor: 'categoryLabel',
            title: t('common.category', 'Catégorie'),
            sortable: true,
            hidden: isMobile,
            render: record => {
              if (record.kind === 'transfer') {
                return <IconArrowsLeftRight size={24} color="gray" />;
              }
              const Icon = getCategoryIcon(getMainCategoryForType(record.type), record.isIncome);
              return (
                <Badge className={styles.badge} leftSection={<Icon size={12} />}>
                  {t(`expense.type.${record.type}`)}
                </Badge>
              );
            },
          },
          {
            accessor: 'sharesCount',
            title: t('common.shares', 'Répartition'),
            sortable: true,
            hidden: isMobile,
            render: record => {
              if (record.kind === 'transfer') return null;
              const shares = record.shares || [];
              return (
                <Avatar.Group>
                  {shares.slice(0, 5).map(share => (
                    <ShareAvatar key={share.id} share={share} size="sm" />
                  ))}
                  {shares.length > 5 && <Avatar size={28}>{`+${shares.length - 5}`}</Avatar>}
                </Avatar.Group>
              );
            },
          },
          {
            accessor: 'statusRank',
            title: t('common.status', 'Statut'),
            sortable: true,
            width: isMobile ? 40 : 130,
            textAlign: 'center',
            render: record => {
              if (record.status === 'NOT_APPLICABLE') {
                return (
                  <Text size="sm" c="dimmed">
                    —
                  </Text>
                );
              }

              const isSettled = record.status === 'SETTLED';
              const isPartial = record.status === 'PARTIAL';
              // Partial reads as a count, never as a bare colour: "2/5" is the only thing
              // that tells a co-owner how far the expense actually is.
              // A transfer never carries shares; narrowing keeps the union honest.
              const settledShares =
                record.kind === 'transfer' ? 0 : (record.settledSharesCount ?? 0);
              const totalShares =
                record.kind === 'transfer' ? 0 : (record.totalSharesCount ?? record.sharesCount);
              const label = isSettled
                ? t('expenses.status.reimbursed', 'Réglé')
                : isPartial
                  ? t('expenses.status.partiallyReimbursed', 'Remboursé {{settled}}/{{total}}', {
                      settled: settledShares,
                      total: totalShares,
                    })
                  : t('expenses.status.toReimburse', 'En attente');
              const color = isSettled
                ? 'var(--mantine-color-teal-6)'
                : isPartial
                  ? 'var(--mantine-color-blue-6)'
                  : 'var(--mantine-color-orange-6)';
              const Icon = isSettled ? IconCircleCheck : IconClock;

              // Mobile keeps the compact pill (the label would not fit), desktop spells the
              // status out so the icon is not the only thing carrying the meaning.
              if (isMobile) {
                return (
                  <Tooltip label={label}>
                    <span className={styles.statusIcon}>
                      <Icon size={18} color={color} />
                    </span>
                  </Tooltip>
                );
              }

              return (
                <div className={styles.statusCell}>
                  <Icon size={18} color={color} />
                  <Text size="sm" style={{ color }}>
                    {label}
                  </Text>
                </div>
              );
            },
          },
          {
            accessor: 'attachmentsCount',
            title: (
              <span aria-label={t('expenses.list.attachmentsColumn', 'Justificatif')}>
                <IconPaperclip size={18} color="var(--mantine-color-gray-6)" />
              </span>
            ),
            sortable: true,
            hidden: isMobile,
            width: 56,
            textAlign: 'center',
            render: record => {
              if (record.kind === 'transfer') return null;
              const count = record.attachmentsCount ?? 0;
              if (count === 0) return null;
              const isRowLoadingReceipt = quickViewExpenseId === record.id && isQuickViewLoading;
              return (
                <Tooltip
                  label={t('expenses.list.attachmentsCount', '{{count}} justificatif(s)', {
                    count,
                  })}
                >
                  <ActionIcon
                    variant="transparent"
                    size={40}
                    loading={isRowLoadingReceipt}
                    aria-label={t('expenses.list.openReceipt', 'Voir le justificatif')}
                    onClick={event => handleReceiptClick(event, record.id, count)}
                  >
                    <IconPaperclip size={18} color="var(--mantine-color-gray-6)" />
                  </ActionIcon>
                </Tooltip>
              );
            },
          },
          {
            accessor: 'amount',
            title: t('common.amount', 'Montant'),
            sortable: true,
            render: record => {
              let amountText = `${record.amount}€`;
              const color = 'inherit';
              const weight = 500;

              if (record.kind === 'expense') {
                amountText = `- ${record.amount}€`;
              } else if (record.kind === 'income') {
                amountText = `+ ${record.amount}€`;
              }

              return (
                <div className={styles.amount} style={{ color, fontWeight: weight }}>
                  {amountText}
                </div>
              );
            },
          },
          {
            accessor: 'netImpact',
            title: t('budget.personalShare', 'Pour moi'),
            sortable: true,
            render: record => {
              if (record.netImpact === null) {
                return (
                  <Text size="sm" c="dimmed">
                    —
                  </Text>
                );
              }

              const tooltip = buildNetImpactTooltip(record, t, currentUserId);

              if (record.netImpact === 0) {
                return (
                  <Tooltip label={tooltip} multiline w={260}>
                    <Text size="sm" c="dimmed">
                      0 €
                    </Text>
                  </Tooltip>
                );
              }

              const color =
                record.netImpact > 0 ? 'var(--mantine-color-teal-6)' : 'var(--mantine-color-red-6)';
              const formatted =
                record.netImpact > 0
                  ? `+ ${record.netImpact.toFixed(2)}€`
                  : `${record.netImpact.toFixed(2)}€`;

              return (
                <Tooltip label={tooltip} multiline w={260}>
                  <div className={styles.amount} style={{ color, fontWeight: 500 }}>
                    {formatted}
                  </div>
                </Tooltip>
              );
            },
          },
          {
            accessor: 'actions',
            title: '',
            width: 90,
            render: record => {
              const isTransfer = record.kind === 'transfer';

              if (!isTransfer && record.isVirtual) {
                if (!record.templateExpenseId) return null;
                const virtualActions: RowAction[] = [
                  {
                    key: 'editAmount',
                    label: t('expenses.menu.editAmount', 'Modifier le montant'),
                    icon: <IconPencil size={18} />,
                    onClick: () => setEditingOccurrence(record),
                  },
                ];
                return <RowActions actions={virtualActions} />;
              }

              // The recurrence template row (isRecurring) can't be edited individually.
              // A materialized occurrence (recurrenceSeriesId set but isRecurring false)
              // is its own independent record and can be edited like any other expense,
              // without affecting the series or other occurrences.
              const isUneditableRecurringRow = !isTransfer && record.isRecurring;

              if (isUneditableRecurringRow) return null;
              if (!isTransfer && record.isReimbursed) return null;

              const actions: RowAction[] = [
                {
                  key: 'edit',
                  label: t('common.edit', 'Modifier'),
                  icon: <IconPencil size={18} />,
                  // Un remboursement se modifie désormais aussi : montant, date, et les
                  // dépenses qu'il règle. Décocher l'une d'elles recrédite sa part.
                  onClick: () =>
                    navigate(isTransfer ? `${record.id}/edit-transfer` : `${record.id}/edit`),
                },
                {
                  key: 'delete',
                  label: t('common.delete', 'Supprimer'),
                  icon: <IconTrash size={18} />,
                  variant: 'danger',
                  disabled: assetAboveQuotas,
                  onClick: () =>
                    isTransfer ? handleDeleteTransfer(record.id) : handleDeleteExpense(record.id),
                },
              ];

              return <RowActions actions={actions} />;
            },
            textAlign: 'right',
          },
        ]}
      />
      {fetchingMore && (
        <div className={styles.fetchingMore}>
          <Loader size="sm" />
        </div>
      )}
      {selectedExpenses.length > 0 && (
        <div className={styles.bannerWrapper}>
          <BudgetSelectBanner
            count={selectedExpenses.length}
            totalAmount={selectedTotalAmount}
            onMarkClick={() => setIsMarkReimbursedModalOpen(true)}
          />
        </div>
      )}
      <MarkReimbursedModal
        open={isMarkReimbursedModalOpen}
        onClose={() => setIsMarkReimbursedModalOpen(false)}
        count={selectedExpenses.length}
        totalAmount={selectedTotalAmount}
        loading={isMarkReimbursedLoading}
        onConfirm={handleMarkReimbursed}
      />
      <MoneyTransferDetailModal
        open={!!detailTransfer}
        onClose={() => setDetailTransfer(null)}
        transfer={detailTransfer}
      />
      <EditOccurrenceAmountModal
        open={!!editingOccurrence}
        onClose={() => setEditingOccurrence(null)}
        title={editingOccurrence?.kind !== 'transfer' ? (editingOccurrence?.title ?? '') : ''}
        initialAmount={editingOccurrence?.amount ?? 0}
        loading={isSetOccurrenceAmountLoading}
        onConfirm={handleConfirmOccurrenceAmount}
      />
      {/* Receipt quick-open modal: the outer Modal hosts the grid so a co-owner can
          navigate between several receipts; AttachmentsViewer pops its own document
          viewer on mount for the first one. */}
      <Modal
        opened={isViewerOpen}
        onClose={() => setQuickViewExpenseId(null)}
        title={quickViewExpense?.title ?? ''}
        size="lg"
      >
        {quickViewExpense && (
          <AttachmentsViewer
            key={quickViewExpense.id}
            attachments={quickViewExpense.attachments}
            openFirstOnMount
          />
        )}
      </Modal>
    </>
  );
};

export default BudgetList;
